import { Dropdown, Table, Button, Form } from "react-bootstrap";
import { useCallback, useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { PropagateLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import { useTranslate } from "../hooks/LanguageProvider";

import type { FRQItem } from "../types/FRQItem";
import { countRfqOffers, searchRfqOffers } from "../services/Rfq/rfqsService";
import { formatToTimestamp } from "../utilities/functions";

type RFQStatus = "submitted" | "shortlisted" | "rejected" | "accepted";

export default function RFQList() {
  const navigate = useNavigate();
  const { translate } = useTranslate();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<FRQItem[]>([]);
  const [statsItems, setStatsItems] = useState<FRQItem[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | RFQStatus>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [limitValue, setLimitValue] = useState(10);
  const [pageCount, setPageCount] = useState(0);
  const [total, setTotal] = useState(0);

  const [sortColumn, setSortColumn] = useState<string>("submitted_at");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");

  const rawUser = localStorage.getItem("user");
  const parsedUser = rawUser ? JSON.parse(rawUser) : null;

  const fetchOffers = useCallback(
    async (
      page = currentPage,
      limit = limitValue,
      currentSearch = search,
      currentStatusFilter = statusFilter,
      column = sortColumn,
      sort = sortDirection
    ) => {
      try {
        setLoading(true);

        const params = {
          limitValue: limit,
          currentPage: page,
          search: currentSearch,
          statusFilter: currentStatusFilter,
          column,
          sort,
          id_user: parsedUser?.id_user || 0,
          id_role: parsedUser?.id_role || 0,
        };

        const [rows, totalData] = await Promise.all([
          searchRfqOffers(params),
          countRfqOffers(params),
        ]);

        setItems(Array.isArray(rows) ? rows : []);

        const totalValue = Number(totalData?.total || 0);
        setTotal(totalValue);
        setPageCount(Math.ceil(totalValue / limit));
      } catch (error) {
        console.error("Error loading RFQ offers:", error);
        setItems([]);
        setTotal(0);
        setPageCount(0);
      } finally {
        setLoading(false);
      }
    },
    [
      currentPage,
      limitValue,
      search,
      statusFilter,
      sortColumn,
      sortDirection,
      parsedUser?.id_user,
      parsedUser?.id_role,
    ]
  );

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const params = {
          limitValue: 1000,
          currentPage: 1,
          search,
          statusFilter,
          column: sortColumn,
          sort: sortDirection,
          id_user: parsedUser?.id_user || 0,
          id_role: parsedUser?.id_role || 0,
        };

        const rows = await searchRfqOffers(params);
        setStatsItems(Array.isArray(rows) ? rows : []);
      } catch (error) {
        console.error("Error loading RFQ stats:", error);
        setStatsItems([]);
      }
    };

    loadStats();
  }, [
    search,
    statusFilter,
    sortColumn,
    sortDirection,
    parsedUser?.id_user,
    parsedUser?.id_role,
  ]);

  const formatPrice = (value: number, currency: string) => {
    return (
      new Intl.NumberFormat("fr-FR", {
        maximumFractionDigits: 0,
      }).format(value || 0) + ` ${currency || "EUR"}`
    );
  };

  const normalizeStatus = (status?: string): RFQStatus => {
    const value = String(status || "").trim().toLowerCase();

    if (value === "accepted") return "accepted";
    if (value === "rejected") return "rejected";
    if (value === "shortlisted") return "shortlisted";
    return "submitted";
  };

  const getStatusBadge = (status: string) => {
    const normalized = normalizeStatus(status);

    const config = {
      submitted: {
        label: translate("submitted"),
        bg: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
        color: "#1d4ed8",
        border: "#93c5fd",
        dot: "#3b82f6",
      },
      shortlisted: {
        label: translate("shortlisted"),
        bg: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
        color: "#b45309",
        border: "#fcd34d",
        dot: "#f59e0b",
      },
      rejected: {
        label: translate("rejected"),
        bg: "linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)",
        color: "#be123c",
        border: "#fda4af",
        dot: "#f43f5e",
      },
      accepted: {
        label: translate("accepted"),
        bg: "linear-gradient(135deg, #ecfdf3 0%, #d1fae5 100%)",
        color: "#166534",
        border: "#86efac",
        dot: "#22c55e",
      },
    };

    const current = config[normalized];

    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          padding: "6px 14px",
          borderRadius: "999px",
          background: current.bg,
          color: current.color,
          border: `1px solid ${current.border}`,
          fontSize: "12px",
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: current.dot,
          }}
        />
        {current.label}
      </span>
    );
  };

  const getScoreBadge = (score: number) => {
    let bg = "#f3f4f6";
    let color = "#374151";

    if (score >= 90) {
      bg = "#dcfce7";
      color = "#166534";
    } else if (score >= 75) {
      bg = "#fef3c7";
      color = "#92400e";
    } else {
      bg = "#fee2e2";
      color = "#991b1b";
    }

    return (
      <span
        style={{
          display: "inline-block",
          minWidth: "56px",
          textAlign: "center",
          padding: "6px 10px",
          borderRadius: "10px",
          background: bg,
          color,
          fontWeight: 700,
          fontSize: "12px",
        }}
      >
        {score || 0}%
      </span>
    );
  };

  const handleSortingColumn = async (column: string) => {
    const nextSort =
      sortColumn === column
        ? sortDirection === "ASC"
          ? "DESC"
          : "ASC"
        : "ASC";

    setSortColumn(column);
    setSortDirection(nextSort);
    setCurrentPage(1);

    await fetchOffers(1, limitValue, search, statusFilter, column, nextSort);
  };

  const handlePageClick = async (data: { selected: number }) => {
    const nextPage = data.selected + 1;
    setCurrentPage(nextPage);
    await fetchOffers(
      nextPage,
      limitValue,
      search,
      statusFilter,
      sortColumn,
      sortDirection
    );
    window.scrollTo(0, 0);
  };

  const handleSearchChange = async (value: string) => {
    setSearch(value);
    setCurrentPage(1);
    await fetchOffers(
      1,
      limitValue,
      value,
      statusFilter,
      sortColumn,
      sortDirection
    );
  };

  const handleLimitChange = async (value: number) => {
    setLimitValue(value);
    setCurrentPage(1);
    await fetchOffers(
      1,
      value,
      search,
      statusFilter,
      sortColumn,
      sortDirection
    );
  };

  const handleStatusChange = async (value: "all" | RFQStatus) => {
    setStatusFilter(value);
    setCurrentPage(1);
    await fetchOffers(
      1,
      limitValue,
      search,
      value,
      sortColumn,
      sortDirection
    );
  };

  const scoreCard = (
    title: string,
    value: number | string,
    icon: string,
    bg: string
  ) => (
    <div className="col-md-3 mb-3">
      <div
        className="card border-0 shadow-sm h-100"
        style={{ borderRadius: "18px", overflow: "hidden" }}
      >
        <div className="card-body d-flex align-items-center justify-content-between">
          <div>
            <div
              style={{ fontSize: "13px", color: "#6b7280", marginBottom: 4 }}
            >
              {title}
            </div>
            <div
              style={{ fontSize: "24px", fontWeight: 700, color: "#111827" }}
            >
              {value}
            </div>
          </div>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#111827",
              fontSize: 22,
            }}
          >
            <i className={icon}></i>
          </div>
        </div>
      </div>
    </div>
  );

  const totalSubmitted = total;

  const totalAccepted = statsItems.filter(
    (i) => normalizeStatus(i.status) === "accepted"
  ).length;

  const avgAiScore =
    statsItems.length > 0
      ? Math.round(
          statsItems.reduce((sum, item) => sum + (item.ai_score || 0), 0) /
            statsItems.length
        )
      : 0;

  const totalMessages = statsItems.reduce(
    (sum, item) => sum + (item.messages_count || 0),
    0
  );

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        {scoreCard(
          translate("total-rfqs"),
          totalSubmitted,
          "fas fa-file-signature",
          "#fef3c7"
        )}
        {scoreCard(
          translate("accepted"),
          totalAccepted,
          "fas fa-circle-check",
          "#dcfce7"
        )}
        {scoreCard(
          translate("average-ai-score"),
          `${avgAiScore}%`,
          "fas fa-star",
          "#dbeafe"
        )}
        {scoreCard(
          translate("messages"),
          totalMessages,
          "fas fa-comments",
          "#ede9fe"
        )}
      </div>

      <div className="row mb-3">
        <div className="col-md-6">
          <h4 className="mb-0">
            <i className="las la-file-invoice-dollar me-2"></i>
            {translate("rfqs")} ({total})
          </h4>
          <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "4px" }}>
            {translate("supplier_offers_submitted_on_oem_rfqs")}
          </div>
        </div>

        <div className="col-md-6 text-end">
          <Button
            variant="warning"
            onClick={() => navigate("/new-rfq")}
            style={{ fontWeight: 600 }}
          >
            + {translate("new_rfq")}
          </Button>
        </div>
      </div>

      <div
        className="card border-0 shadow-sm mb-3"
        style={{ borderRadius: "18px" }}
      >
        <div className="card-body">
          <div className="row g-3 align-items-center">
            <div className="col-md-5">
              <Form.Control
                type="text"
                placeholder={translate("search_by_rfs_oem_supplier")}
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <Form.Select
                value={statusFilter}
                onChange={(e) =>
                  handleStatusChange(e.target.value as "all" | RFQStatus)
                }
              >
                <option value="all">({translate("status")})</option>
                <option value="submitted">({translate("submitted")})</option>
                <option value="shortlisted">({translate("shortlisted")})</option>
                <option value="accepted">({translate("accepted")})</option>
                <option value="rejected">({translate("rejected")})</option>
              </Form.Select>
            </div>

            <div className="col-md-2">
              <Form.Select
                value={limitValue}
                onChange={(e) => handleLimitChange(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </Form.Select>
            </div>

            <div className="col-md-2 text-end">
              <Dropdown>
                <Dropdown.Toggle variant="light">
                  <i className="las la-filter me-1"></i>
                  {translate("sort")}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => handleSortingColumn("submitted_at")}
                  >
                    {translate("submitted_date")}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSortingColumn("price")}>
                    {translate("price")}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSortingColumn("ai_score")}>
                    {translate("score")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => handleSortingColumn("compliance_score")}
                  >
                    {translate("profile_completion")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => handleSortingColumn("delivery_time_days")}
                  >
                    {translate("delivery_time")}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>
      </div>

      <Table responsive hover className="dataTable role-table mt-3 align-middle">
        <thead>
          <tr className="ligth ligth-data">
            <th
              onClick={() => handleSortingColumn("rfq_title")}
              style={{ cursor: "pointer" }}
            >
              {translate("rfq")}
            </th>
            <th
              onClick={() => handleSortingColumn("oem_name")}
              style={{ cursor: "pointer" }}
            >
              {translate("oem")}
            </th>
            <th
              onClick={() => handleSortingColumn("supplier_name")}
              style={{ cursor: "pointer" }}
            >
             {translate("supplier")}
            </th>
            <th
              onClick={() => handleSortingColumn("price")}
              style={{ cursor: "pointer" }}
            >
               {translate("price")}
            </th>
            <th
              onClick={() => handleSortingColumn("delivery_time_days")}
              style={{ cursor: "pointer" }}
            >
              Delivery
            </th>
            <th
              onClick={() => handleSortingColumn("capacity")}
              style={{ cursor: "pointer" }}
            >
              {translate("capacity")}
            </th>
            <th
              onClick={() => handleSortingColumn("compliance_score")}
              style={{ cursor: "pointer" }}
            >
              {translate("profile_completion")}
            </th>
            <th
              onClick={() => handleSortingColumn("ai_score")}
              style={{ cursor: "pointer" }}
            >
              {translate("score")}
            </th>
            <th>{translate("activity")}</th>
            <th>{translate("status")}</th>
            <th
              onClick={() => handleSortingColumn("submitted_at")}
              style={{ cursor: "pointer" }}
            >
              {translate("submitted")}
            </th>
            <th>{translate("actions")}</th>
          </tr>
        </thead>

        <tbody className="ligth-body">
          {loading ? (
            <tr>
              <td className="text-center" colSpan={12}>
                <PropagateLoader size={12} />
              </td>
            </tr>
          ) : items.length > 0 ? (
            items.map((item) => (
              <tr key={item.id}>
                <td>
                  <div style={{ minWidth: 220 }}>
                    <div style={{ fontWeight: 700, color: "#111827" }}>
                      {item.rfq_title}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      RFQ #{item.rfq_id}
                    </div>
                  </div>
                </td>

                <td>
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {item.oem?.name || "-"}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      {item.oem?.country || "-"}
                    </div>
                  </div>
                </td>

                <td>
                  <div>
                
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      {item.supplier?.total_for_rfq ?? 0} suppliers
                    </div>
                  </div>
                </td>

                <td style={{ fontWeight: 700 }}>
                  {formatPrice(item.price || 0, item.currency || "EUR")}
                </td>
                <td>{item.delivery_time_days || 0} days</td>
                <td>{Number(item.capacity || 0).toLocaleString("fr-FR")}</td>
                <td>{getScoreBadge(item.compliance_score || 0)}</td>
                <td>{getScoreBadge(item.ai_score || 0)}</td>

                <td>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <span className="badge bg-light text-dark">
                      <i className="fas fa-comments me-1"></i>
                      {item.messages_count || 0}
                    </span>
                    <span className="badge bg-light text-dark">
                      <i className="fas fa-paperclip me-1"></i>
                      {item.documents_count || 0}
                    </span>
                  </div>
                </td>

                <td>{(getStatusBadge(item.status))}</td>
                <td>{formatToTimestamp(item.submitted_at) || "-"}</td>

                <td className="text-center">
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      className="btn btn-sm rounded-circle"
                      style={{
                        width: 38,
                        height: 38,
                        backgroundColor: "#020815",
                        border: "1px solid #020815",
                        color: "#ffffff",
                      }}
                      title="View RFQ"
                    >
                      <i className="fas fa-eye"></i>
                    </button>

                    <button
                      className="btn btn-sm btn-warning rounded-circle"
                      style={{ width: 38, height: 38 }}
                      title="Open discussion"
                    >
                      <i className="fas fa-comments"></i>
                    </button>

                    <button
                      className="btn btn-sm btn-success rounded-circle"
                      style={{ width: 38, height: 38 }}
                      title="Shortlist / Accept"
                    >
                      <i className="fas fa-check"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={12} className="text-center">
                No RFQs available
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between mt-3 align-items-center">
        <span>
          {translate("Displaying")} {items.length} {translate("out of")} {total}
        </span>

        <ReactPaginate
          previousLabel={translate("previous")}
          nextLabel={translate("next")}
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={2}
          pageRangeDisplayed={3}
          onPageChange={handlePageClick}
          containerClassName={"pagination justify-content-end mb-0"}
          pageClassName={"page-item"}
          pageLinkClassName={"page-link"}
          previousClassName={"page-item"}
          previousLinkClassName={"page-link"}
          nextClassName={"page-item"}
          nextLinkClassName={"page-link"}
          breakClassName={"page-item"}
          breakLinkClassName={"page-link"}
          activeClassName={"active"}
          forcePage={currentPage - 1}
        />
      </div>
    </div>
  );
}